import { decodeJwt, createRemoteJWKSet, jwtVerify } from 'jose';
import { ssoClient } from '@better-auth/sso/client';
import { createAuthClient } from 'better-auth/client';
import { organizationClient, twoFactorClient, jwtClient, apiKeyClient, adminClient, inferAdditionalFields, inferOrgAdditionalFields } from 'better-auth/client/plugins';
import { createAccessControl } from 'better-auth/plugins/access';
import { defaultStatements } from 'better-auth/plugins/organization/access';
export { BetterFetchError as APIError } from '@better-fetch/fetch';

const version = "1.4.0";

const statements = {
  ...defaultStatements,
  access: ["admin", "member", "reviewer"]
};
const ac = createAccessControl(statements);
const Roles = {
  ORG_ADMIN: "org:admin",
  ORG_MEMBER: "org:member",
  ORG_REVIEWER: "org:reviewer"
};
const rolesAccessControl = {
  [Roles.ORG_ADMIN]: ac.newRole({
    access: ["admin"],
    organization: ["update"],
    team: ["create", "update", "delete"],
    member: ["create", "update", "delete"],
    invitation: ["create", "cancel"]
  }),
  [Roles.ORG_MEMBER]: ac.newRole({
    access: ["member"]
  }),
  [Roles.ORG_REVIEWER]: ac.newRole({
    access: ["reviewer"]
  })
};
const userAdditionalFields = {
  lastActiveAt: {
    type: "date",
    required: false,
    defaultValue: null
  }
};
const organizationAdditionalFields = {
  settings: {
    type: "json",
    input: true,
    required: false
  }
};
const memberAdditionalFields = {
  deletedAt: {
    type: "date",
    required: false,
    defaultValue: null
  }
};

function applicationsPluginClient() {
  return {
    id: "applications",
    getActions: ($fetch) => {
      return {
        applications: {
          listCandidateOrganizations: async (applicationId) => {
            const response = await $fetch("/applications/candidate-organizations", {
              query: {
                applicationId
              }
            });
            const organizations = response.data?.organizations.map((organization) => ({
              id: organization.id,
              name: organization.title,
              slug: organization.slug ?? "",
              createdAt: organization.createdAt,
              logo: organization.avatarUrl,
              metadata: organization.metadata
            })) ?? [];
            return {
              ...response,
              data: {
                ...response.data,
                organizations
              }
            };
          },
          startAuthorizationFlow: async (applicationId, redirectUri, codeChallenge, organizationId) => {
            return await $fetch("/applications/authorize", {
              method: "POST",
              body: {
                applicationId,
                redirectUri,
                codeChallenge,
                organizationId
              }
            });
          },
          completeAuthorizationFlow: async (code, codeVerifier) => {
            return await $fetch("/applications/token", {
              method: "POST",
              body: {
                code,
                codeVerifier
              }
            });
          },
          refreshAccessToken: async (refreshToken) => {
            const headers = new Headers();
            if (refreshToken) {
              headers.set("Cookie", `tela-refresh-token=${refreshToken}`);
            }
            return await $fetch("/applications/token/refresh", {
              method: "POST",
              headers
            });
          },
          whoAmI: async (accessToken) => {
            const headers = new Headers();
            if (accessToken) {
              headers.set("Cookie", `tela-access-token=${accessToken}`);
            }
            return await $fetch("/applications/whoami", {
              method: "GET",
              headers
            });
          }
        }
      };
    }
  };
}

function customEndpointsPluginClient() {
  return {
    id: "custom-endpoints",
    $InferServerPlugin: {}
  };
}
function handshakePluginClient() {
  return {
    id: "handshake",
    $InferServerPlugin: {}
  };
}
function createAPIClient(apiUrl, fetchOptions = {}) {
  const serviceName = typeof process !== "undefined" ? process.env.SERVICE_NAME : "";
  const userAgent = `auth-sdk:core:${version}${serviceName ? `@${serviceName}` : ""}`;
  return createAuthClient({
    baseURL: apiUrl,
    plugins: [
      organizationClient({
        ac,
        roles: rolesAccessControl,
        teams: {
          enabled: true
        },
        schema: inferOrgAdditionalFields({
          // TODO: check if this is fixed in the next version
          //     organization: {
          //         additionalFields: {
          //             settings: {
          //                 type: 'json' as const,
          //                 input: true,
          //                 required: false,
          //             }
          //         },
          //     }
          member: {
            additionalFields: memberAdditionalFields
          }
        })
      }),
      customEndpointsPluginClient(),
      handshakePluginClient(),
      ssoClient(),
      twoFactorClient(),
      jwtClient(),
      apiKeyClient(),
      adminClient(),
      inferAdditionalFields({
        user: userAdditionalFields
      }),
      applicationsPluginClient()
    ],
    fetchOptions: {
      ...fetchOptions,
      credentials: "include",
      headers: {
        "X-User-Agent": userAgent,
        ...fetchOptions.headers ?? {}
      },
      throw: true
    }
  });
}
createAPIClient("");

class BaseError extends Error {
  code;
  constructor(code, message, options) {
    super(message, options);
    this.code = code;
  }
}

class ApplicationError extends BaseError {
  constructor(message, options) {
    super("APPLICATION_ERROR", message, options);
  }
}
class RefreshTokenExpiredError extends ApplicationError {
  constructor(options) {
    super("Refresh token has expired or is invalid", options);
    this.code = "REFRESH_TOKEN_EXPIRED";
  }
}
class AuthorizationFlowError extends ApplicationError {
  constructor(message, options) {
    super(message, options);
    this.code = "AUTHORIZATION_FLOW_ERROR";
  }
}

class ApplicationService {
  /**
   * Creates a new ApplicationService instance.
   *
   * @param client - The API client for making application requests
   */
  constructor(client) {
    this.client = client;
  }
  /**
   * Lists candidate organizations for a specific application.
   *
   * Returns organizations where the authenticated user is a member and
   * the application has been enabled with appropriate entitlement rules.
   *
   * @param applicationId - The application ID to get candidate organizations for
   * @returns The application details and list of candidate organizations
   */
  async listCandidateOrganizations(applicationId) {
    const response = await this.client.applications.listCandidateOrganizations(applicationId);
    if (!response.data) {
      throw new Error("No data returned from the API", { cause: response.error });
    }
    return response.data;
  }
  /**
   * Starts an authorization flow for a specific application.
   *
   * @param applicationId - The application ID to start the authorization flow for
   * @param redirectUri - The redirect URI to use for the authorization flow
   * @param codeChallenge - The code challenge to use for the authorization flow
   * @param organizationId - The organization ID to start the authorization flow for
   */
  async startAuthorizationFlow(applicationId, redirectUri, codeChallenge, organizationId) {
    const response = await this.client.applications.startAuthorizationFlow(applicationId, redirectUri, codeChallenge, organizationId);
    if (!response.data) {
      throw new Error("No data returned from the API", { cause: response.error });
    }
    return response.data;
  }
  /**
   * Completes an authorization flow for a specific application.
   *
   * @param code - The code to use for the authorization flow
   * @param codeVerifier - The code verifier to use for the authorization flow
   */
  async completeAuthorizationFlow(code, codeVerifier) {
    const response = await this.client.applications.completeAuthorizationFlow(code, codeVerifier);
    if (!response.data) {
      throw new Error("No data returned from the API", { cause: response.error });
    }
    return response.data;
  }
  /**
   * Refreshes an authentication token for a specific application.
   *
   * @param refreshToken - The refresh token to use for the authentication token refresh
   * @throws {RefreshTokenExpiredError} When the refresh token has expired or is invalid
   * @throws {ApplicationError} For other API errors
   */
  async refreshAccessToken() {
    const response = await this.client.applications.refreshAccessToken();
    if (!response.data) {
      const error = response.error;
      const status = error?.status;
      if (status === 404) {
        throw new RefreshTokenExpiredError({ cause: error });
      }
      throw new ApplicationError(error?.message || "Failed to refresh access token", { cause: error });
    }
    return response.data;
  }
  /**
   * Gets the current user and organization for a specific application.
   *
   * @param accessToken - The access token to use for the who am I request
   * @returns The current user and organization
   */
  async whoAmI(accessToken) {
    const response = await this.client.applications.whoAmI(accessToken);
    if (!response.data) {
      throw new Error("No data returned from the API", { cause: response.error });
    }
    return response.data;
  }
}

class OrganizationService {
  /**
   * Creates a new OrganizationService instance.
   *
   * @param client - The API client for making organization requests
   */
  constructor(client) {
    this.client = client;
  }
  /**
   * Retrieves a single organization by ID with optional related data.
   *
   * @param id - The organization ID
   * @returns The organization with members, invitations, and teams
   */
  async getOrganization(id) {
    const organization = await this.client.organization.getFullOrganization({
      query: {
        organizationId: id
      }
    });
    return organization;
  }
  /**
   * Lists all organizations accessible to the current user.
   *
   * @returns An array of organizations
   */
  async listOrganizations() {
    const organizations = await this.client.organization.list();
    return organizations;
  }
  /**
   * Sets the active organization for the current user session.
   *
   * @param id - The organization ID to set as active
   */
  async setActiveOrganization(id) {
    await this.client.organization.setActive({
      organizationId: id
    });
  }
  /**
   * Updates an organization's details.
   *
   * @param payload - The organization fields to update
   * @param payload.name - New organization name
   * @param payload.logo - New organization logo URL
   * @param payload.settings - New organization settings
   * @returns The updated organization
   */
  async updateOrganization(payload) {
    const organization = await this.client.organization.update({
      data: {
        ...payload,
        logo: payload.logo ?? void 0
      }
    });
    return organization;
  }
  /**
   * Lists members of the active organization with optional pagination.
   *
   * @param options - Pagination options
   * @param options.limit - Maximum number of members to return
   * @param options.offset - Number of members to skip
   * @returns An array of organization members
   */
  async listMembers(options) {
    const members = await this.client.organization.listMembers({
      query: options
    });
    return members.members;
  }
  /**
   * Gets the currently active organization member for the authenticated user.
   *
   * @returns The active member object
   */
  async getActiveMember() {
    return await this.client.organization.getActiveMember();
  }
  /**
   * Invites a user to join the active organization.
   *
   * @param options - Invitation configuration
   * @param options.userEmail - Email address of the user to invite
   * @param options.role - Role to assign to the invited user
   * @param options.teamId - Team ID to add the user to
   * @param options.resend - Whether to resend if invitation already exists
   * @returns The created invitation
   */
  async inviteUserToOrganization({ userEmail, role, teamId, resend }) {
    return await this.client.organization.inviteMember({
      email: userEmail,
      role,
      teamId,
      resend: resend ?? false
    });
  }
  /**
   * Cancels a pending organization invitation.
   *
   * @param id - The invitation ID to cancel
   */
  async cancelInvitation(id) {
    await this.client.organization.cancelInvitation({
      invitationId: id
    });
  }
  /**
   * Accepts an organization invitation.
   *
   * @param id - The invitation ID to accept
   */
  async acceptInvitation(id) {
    await this.client.organization.acceptInvitation({
      invitationId: id
    });
  }
  /**
       * Removes a user from the active organization.
       *
       * @param options - User identifier (either memberId or userEmail must be provided)
       * @param options.memberId - The member ID to remove
       * @param options.userEmail - The user email to remove
  
       */
  async removeUserFromOrganization({ memberId, userEmail }) {
    await this.client.organization.removeMember({
      memberIdOrEmail: memberId ?? userEmail
    });
  }
  /**
   * Updates the role of an organization member.
   *
   * @param options - Role update configuration
   * @param options.memberId - The member ID to update
   * @param options.role - The new role to assign
   */
  async updateMemberRole({ memberId, role }) {
    await this.client.organization.updateMemberRole({
      memberId,
      role
    });
  }
  /**
   * Creates a new team within the active organization.
   *
   * @param payload - Team configuration
   * @param payload.name - The name of the team
   * @returns The created team
   */
  async createTeam(payload) {
    return await this.client.organization.createTeam(payload);
  }
  /**
   * Updates an existing team's details.
   *
   * @param id - The team ID to update
   * @param payload - Team fields to update
   * @param payload.name - The new team name
   * @returns The updated team
   */
  async updateTeam(id, payload) {
    return await this.client.organization.updateTeam({
      teamId: id,
      data: payload
    });
  }
  /**
   * Deletes a team from the active organization.
   *
   * @param id - The team ID to delete
   */
  async deleteTeam(id) {
    await this.client.organization.removeTeam({
      teamId: id
    });
  }
  /**
   * Lists all teams in the active organization.
   *
   * @returns An array of teams
   */
  async listTeams() {
    return await this.client.organization.listTeams();
  }
  /**
   * Lists all members of a specific team.
   *
   * @param id - The team ID
   * @returns An array of team members
   */
  async listTeamMembers(id) {
    return await this.client.organization.listTeamMembers({
      query: {
        teamId: id
      }
    });
  }
  /**
   * Adds a user to a team.
   *
   * @param teamId - The team ID
   * @param userId - The user ID to add
   * @returns The added team member
   */
  async addTeamMember(teamId, userId) {
    return await this.client.organization.addTeamMember({
      teamId,
      userId
    });
  }
  /**
   * Removes a user from a team.
   *
   * @param teamId - The team ID
   * @param userId - The user ID to remove
   */
  async removeTeamMember(teamId, userId) {
    await this.client.organization.removeTeamMember({
      teamId,
      userId
    });
  }
}

class InvalidSocialProvider extends BaseError {
  constructor(message) {
    super("INVALID_SOCIAL_PROVIDER", message);
  }
}
class InvalidCallbackURL extends BaseError {
  constructor(message) {
    super("INVALID_CALLBACK_URL", message);
  }
}
class EmailRequired extends BaseError {
  constructor(message) {
    super("EMAIL_REQUIRED", message);
  }
}

function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
}
class SessionService {
  /**
   * Creates a new SessionService instance.
   *
   * @param client - The API client for making authentication requests
   * @param apiUrl - The base URL of the authentication API
   */
  constructor(client, apiUrl) {
    this.client = client;
    this.apiUrl = apiUrl;
  }
  /**
   * Retrieves the current user session.
   *
   * @returns The current user session
   */
  async getSession(token) {
    return await this.client.getSession({
      ...token && {
        fetchOptions: {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      }
    });
  }
  /**
   * Retrieves a valid JWT token.
   *
   * @returns The JWT token
   */
  async getToken() {
    return await this.client.token();
  }
  /**
   * Initiates social authentication flow with Google or Microsoft.
   *
   * @param options - Social sign-in configuration
   * @param options.provider - The social provider
   * @param options.callbackURL - URL to redirect to after successful authentication
   * @param options.errorCallbackURL - URL to redirect to on error
   *
   * @throws {InvalidSocialProvider} When the provider is not 'google' or 'microsoft'
   * @throws {InvalidCallbackURL} When callback URLs are malformed
   */
  async signInWithSocialProvider({
    provider,
    callbackURL,
    errorCallbackURL
  }) {
    if (provider !== "google" && provider !== "microsoft") {
      throw new InvalidSocialProvider("Invalid social provider");
    }
    if (!isValidUrl(callbackURL)) {
      throw new InvalidCallbackURL(`Invalid callback URL: ${callbackURL}`);
    }
    if (errorCallbackURL && !isValidUrl(errorCallbackURL)) {
      throw new InvalidCallbackURL(`Invalid error callback URL: ${errorCallbackURL}`);
    }
    const appUrl = encodeURIComponent(new URL(callbackURL).origin);
    await this.client.signIn.social({
      provider,
      callbackURL,
      errorCallbackURL: errorCallbackURL ?? `${this.apiUrl}/error?callbackURL=${appUrl}`
    });
  }
  /**
   * Initiates SAML-based Single Sign-On authentication flow.
   *
   * @param options - SAML sign-in configuration
   * @param options.email - User's email to determine the SAML provider
   * @param options.callbackURL - URL to redirect to after successful authentication
   * @param options.errorCallbackURL - URL to redirect to on error
   *
   * @throws {EmailRequired} When email is not provided
   * @throws {InvalidCallbackURL} When callback URLs are malformed
   */
  async signInWithSaml({
    email,
    callbackURL,
    errorCallbackURL
  }) {
    if (!email) {
      throw new EmailRequired("Email is required");
    }
    if (!isValidUrl(callbackURL)) {
      throw new InvalidCallbackURL(`Invalid callback URL: ${callbackURL}`);
    }
    if (errorCallbackURL && !isValidUrl(errorCallbackURL)) {
      throw new InvalidCallbackURL(`Invalid error callback URL: ${errorCallbackURL}`);
    }
    const appUrl = encodeURIComponent(new URL(callbackURL).origin);
    await this.client.signIn.sso({
      email,
      callbackURL,
      errorCallbackURL: errorCallbackURL ?? `${this.apiUrl}/error?callbackURL=${appUrl}`,
      providerType: "saml"
    });
  }
  /**
   * Authenticates a user with email and password credentials.
   *
   * @param options - Email/password sign-in configuration
   * @param options.email - User's email address
   * @param options.password - User's password
   */
  async signInWithEmailAndPassword({
    email,
    password
  }) {
    await this.client.signIn.email({
      email,
      password
    });
  }
  /**
   * Signs out the currently authenticated user.
   *
   * @param callback - Callback function to execute after signing out
   */
  async signOut(callback) {
    await this.client.signOut();
    await callback?.();
  }
  /**
   * Initiates a password reset request by sending a reset email.
   *
   * @param email - Email address of the user requesting password reset
   * @param callbackURL - URL where the user will be redirected to complete the reset
   */
  async requestPasswordReset(email, callbackURL) {
    await this.client.requestPasswordReset({
      email,
      redirectTo: callbackURL
    });
  }
  /**
   * Completes the password reset process with a reset token and new password.
   *
   * @param token - The password reset token from the email
   * @param password - The new password to set
   */
  async resetPassword(token, password) {
    await this.client.resetPassword({
      token,
      newPassword: password
    });
  }
  /**
   * Retrieves the JWT token for a nonce.
   *
   * @param nonce - The nonce to retrieve the token for
   * @returns The JWT token for the nonce
   */
  async getNoncePayload(nonce) {
    return await this.client.handshake.noncePayload({
      query: {
        nonce
      }
    });
  }
}

class ApiKeyService {
  /**
   * Creates a new ApiKeyService instance.
   *
   * @param client - The API client for making API key requests
   */
  constructor(client) {
    this.client = client;
  }
  async createApiKey(payload) {
    return await this.client.apiKey.create({
      name: payload.name,
      expiresIn: payload.expiresIn,
      prefix: payload.prefix,
      metadata: payload.metadata
    });
  }
  async getApiKey(id) {
    return await this.client.apiKey.get({
      query: {
        id
      }
    });
  }
  async listApiKeys() {
    return await this.client.apiKey.list();
  }
  async updateApiKey(payload) {
    return await this.client.apiKey.update({
      keyId: payload.id,
      name: payload.name
    });
  }
  async deleteApiKey(id) {
    return await this.client.apiKey.delete({
      keyId: id
    });
  }
}

class AuthClient {
  client;
  /**
   * Session management service for authentication operations
   */
  session;
  /**
   * Organization management service for multi-tenant operations
   */
  organization;
  /**
   * Application management service for application operations
   */
  application;
  /**
   * API key management service for API key operations
   */
  apiKey;
  /**
   * Creates a new AuthClient instance.
   *
   * @param apiUrl - The base URL of the authentication API
   * @param fetchOptions - Custom fetch options to include in all API requests
   */
  constructor(apiUrl, fetchOptions = {}) {
    this.client = createAPIClient(apiUrl, fetchOptions);
    this.session = new SessionService(this.client, apiUrl);
    this.organization = new OrganizationService(this.client);
    this.application = new ApplicationService(this.client);
    this.apiKey = new ApiKeyService(this.client);
  }
}

function isTokenExpired(token, expiresInMs) {
  const payload = JSON.parse(atob(token.split(".")[1] ?? ""));
  if (!payload.exp) return false;
  const nowInSeconds = Date.now() / 1e3;
  const expirationTime = payload.exp;
  if (expiresInMs !== void 0) {
    const futureTimeInSeconds = nowInSeconds + expiresInMs / 1e3;
    return futureTimeInSeconds >= expirationTime;
  }
  return nowInSeconds > expirationTime;
}
async function validateToken(token, apiUrl) {
  if (isTokenExpired(token)) {
    return false;
  }
  try {
    const JWKS = createRemoteJWKSet(
      new URL(`${apiUrl}/api/auth/jwks`)
    );
    await jwtVerify(token, JWKS, {
      issuer: apiUrl
    });
    return true;
  } catch (error) {
    return false;
  }
}
function extractTokenPayload(token) {
  const payload = decodeJwt(token);
  return payload;
}

export { ApplicationError, AuthClient, AuthorizationFlowError, EmailRequired, InvalidCallbackURL, InvalidSocialProvider, RefreshTokenExpiredError, Roles, ac, extractTokenPayload, isTokenExpired, memberAdditionalFields, organizationAdditionalFields, rolesAccessControl, userAdditionalFields, validateToken };
